import {
  type StoreCard,
  type BentoBoxCollectionData,
  type BentoBoxData,
  type RetailerShelfHeaderData,
  type StoreAttributeData,
} from '@/features/cms/components/CMS/types'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { useMemo, useState } from 'react'
import { BentoBox } from '@/features/cms/components/CMS/components/BentoBox'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import styled from 'styled-components'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { screenSizes } from '@shipt/design-system-themes'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { TextButtonLink } from '@/features/shared/elements/Link/Buttons'
import { Box, Column, Row } from '@shipt/design-system-layouts'
import { Body } from '@shipt/design-system-typography'
import { StoreAttribute } from '@/features/cms/components/CMS/components/BrandAmplification/StoreAttribute'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { MultiStoreHeader } from '@/features/cms/components/CMS/components/MultiStoreHeader'
import { BotOnly } from '@/features/shared/components/BotOnly'
import { theme } from '@/features/shared/constants/shiptTheme'

const extractStoresDataFromBentoBoxes = (items: BentoBoxData[]): StoreCard['data'][] => {
  const stores: StoreCard['data'][] = []

  items.forEach((bentoBox) => {
    const header = bentoBox.data.header
    if (header.content_type_id === 'retailer_shelf_header') {
      stores.push(header.data.store.data)
    }
  })

  return stores
}

const getSelectedStoreAndHeaderData = (
  items: BentoBoxData<RetailerShelfHeaderData>[],
  selectedIndex: number,
) => {
  const selectedBox = items[selectedIndex]
  const header = selectedBox?.data.header

  if (header?.content_type_id !== 'retailer_shelf_header') {
    return { headerData: null, store: null }
  }

  const store = header.data.store.data
  return { headerData: header.data, store }
}

const getStoreAttribute = (store?: StoreCard['data'] | null) => {
  if (!store?.primary_attributes) return null

  const primaryLoud = store.primary_attributes.find(
    (attr) => attr.data.treatment === 'Primary Loud',
  )?.data

  const secondaryLoud = store.primary_attributes.find(
    (attr) => attr.data.treatment === 'Secondary Loud',
  )?.data

  return primaryLoud || secondaryLoud || null
}

const SingleStoreHeader = ({
  store,
  attribute,
  link,
}: {
  store: StoreCard['data']
  attribute: StoreAttributeData | null
  link?: {
    url: string
    text: string
  }
}) => {
  const borderColor = '#0000001a'
  const storeCallout = !store.has_markup ? 'No markups' : ''

  return (
    <Row align="center" justify="space-between" padding={{ base: [0, 'lg'], md: [0] }}>
      <Row align="center" spacing="md">
        <StoreLogoTile
          logoBackgroundColor={store.background_color ?? ''}
          borderColor={borderColor}
          aria-label={`${store.name} logo`}
          $width={44}
          $height={44}
          $isSingleStore
        >
          <Image src={store.logo.src} alt={`${store.name} logo`} width={44} height={44} />
        </StoreLogoTile>
        <Column>
          <Row spacing="sm" align="center">
            <Body strong size="xl">
              {store.name}
            </Body>
          </Row>
          {attribute ? (
            <Box>
              <StoreAttribute {...attribute} />
            </Box>
          ) : (
            storeCallout && (
              <Body size="md" variant="secondary">
                {storeCallout}
              </Body>
            )
          )}
        </Column>
      </Row>
      {!!link && (
        <TextButtonLink aria-label={link.text} href={link.url}>
          {link.text}
        </TextButtonLink>
      )}
    </Row>
  )
}

/**
 * This component corresponds to the `bento_box_collection` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/bento_box_collection
 * - **Production** - https://cms.shipt.com/content-type/pool/bento_box_collection
 */
export const BentoBoxCollection = ({ content_type_id, id, data }: BentoBoxCollectionData) => {
  const { items, background_color } = data

  const [selectedStoreIndex, setSelectedStoreIndex] = useState(0)
  const stores = useMemo(() => extractStoresDataFromBentoBoxes(items), [items])
  const { headerData, store: selectedStore } = useMemo(
    () => getSelectedStoreAndHeaderData(items, selectedStoreIndex),
    [items, selectedStoreIndex],
  )

  const isMultiStore = stores.length > 1
  const attributeToShow = getStoreAttribute(selectedStore)
  const selectedBentoBox = items[selectedStoreIndex]

  const { trackCMSElementViewed } = useCMSTrackingEvents()

  if (!items.length) return null

  try {
    return (
      <ScrollIntoView
        onInView={() => {
          trackCMSElementViewed({
            content: 'bento_box_collection',
            type: 'shelf',
            display_format: DISPLAY_FORMAT.BENTO_PROMO_GRID,
          })
        }}
      >
        <RetailerShelfLayout {...getCMSWrapperProps({ id, content_type_id })}>
          {isMultiStore ? (
            <MultiStoreHeader
              stores={stores}
              selectedIndex={selectedStoreIndex}
              onStoreSelect={setSelectedStoreIndex}
              heading={headerData?.heading}
              link={headerData?.link}
            />
          ) : (
            selectedStore && (
              <SingleStoreHeader
                store={selectedStore}
                attribute={attributeToShow}
                link={headerData.link}
              />
            )
          )}

          {!!selectedBentoBox && (
            <BentoBox
              {...selectedBentoBox}
              store={selectedStore || undefined}
              collectionBackgroundColor={background_color}
            />
          )}
          {!!isMultiStore && (
            <BotOnly>
              {items
                .filter((_, index) => index !== selectedStoreIndex)
                .map((bentoBox) => (
                  <div key={bentoBox.id}>
                    <h2>{bentoBox.data.header.data.heading}</h2>
                    <BentoBox
                      {...bentoBox}
                      store={bentoBox.data.header.data.store.data}
                      collectionBackgroundColor={background_color}
                    />
                  </div>
                ))}
            </BotOnly>
          )}
        </RetailerShelfLayout>
      </ScrollIntoView>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const RetailerShelfLayout = styled(GridLayoutContainer)`
  @media ${screenSizes.max_mobile} {
    --padding-inline: 0px;
  }
`

const StoreLogoTile = styled(TransparentButton)<{
  logoBackgroundColor: string
  borderColor: string
  $isSelected?: boolean
  $width?: number
  $height?: number
  $isSingleStore?: boolean
}>`
  background-color: ${({ logoBackgroundColor }) => logoBackgroundColor};
  border-radius: 12px;
  border: 1px solid ${({ borderColor }) => borderColor};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  width: ${({ $width }) => $width || 64}px;
  height: ${({ $height }) => $height || 64}px;

  flex-shrink: 0;
  cursor: ${({ $isSingleStore }) => ($isSingleStore ? 'default' : 'pointer')};

  &::after {
    content: '';
    position: absolute;
    inset: -5px;
    border-radius: 14px;
    border: ${({ $isSelected }) => ($isSelected ? `2px solid ${theme.color.border.base.neutral.heavy}` : '2px solid transparent')};

    transition: border-color 0.2s ease;
    pointer-events: none;
  }

  img {
    object-fit: contain;
    border-radius: 12px;
    transition: transform 0.2s ease;
    transform: ${({ $isSelected }) => ($isSelected ? 'scale(0.9)' : 'scale(1)')};
  }
`
